export type IdentityUser = {
  // Decimal string, not a number: identity user ids are 64-bit integers
  // (see generateUserId in lobbyze-identity), so new ids exceed
  // Number.MAX_SAFE_INTEGER and must round-trip exactly as text.
  id: string
  name: string
  email: string
  role: string
  is_verified: boolean
  exp: number
  iat: number
  iss: string
}

// A token is replaced this long before exp, so a request never leaves with
// a token that expires in flight. The native poller skips tokens with less
// than 90 seconds left, so this stays above that.
export const REFRESH_MARGIN_SECONDS = 120

type IdentityClaims = {
  // Identity always issues `id` as a decimal string (LobbyzeJwtPayload).
  id?: string
  email?: string
  username?: string
  role?: string
  exp?: number
  iat?: number
  iss?: string
}

export function decodeClaims(token: string): IdentityClaims | null {
  try {
    const payload = token.split(".")[1]
    if (!payload) {
      return null
    }
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/")
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=")
    return JSON.parse(atob(padded)) as IdentityClaims
  } catch {
    return null
  }
}

/**
 * The JWT `id` claim as-is, or null when it is not a positive decimal string.
 * It is never coerced with Number(): identity-native ids are 64-bit
 * (`(minutes since epoch << 32) | random`), so Number() loses precision and
 * Number.isSafeInteger rejects them — which made the app drop valid tokens
 * and bounce signed-in users back to identity forever.
 */
export function parseUserId(raw: string | undefined): string | null {
  if (typeof raw !== "string" || !/^[1-9]\d*$/.test(raw)) {
    return null
  }
  return raw
}

export function userFromToken(token: string): IdentityUser | null {
  const claims = decodeClaims(token)
  const id = parseUserId(claims?.id)
  if (!claims || !id || !claims.email || typeof claims.exp !== "number") {
    return null
  }
  return {
    id,
    name: claims.username || "",
    email: claims.email,
    role: claims.role || "user",
    // Identity owns email verification. A user who holds a session passed it.
    is_verified: true,
    exp: claims.exp,
    iat: claims.iat ?? 0,
    iss: claims.iss || "",
  }
}

/**
 * True for the API's RFC 6750 challenge on a token that failed verification.
 * Other 401s mean the user lacks permission or a license.
 */
export function isRejectedTokenChallenge(header: string | null): boolean {
  return !!header && /^\s*bearer\b/i.test(header) && /error\s*=\s*"?invalid_token"?/i.test(header)
}

export type SignInNavigation = "sign-in" | "sign-out-then-sign-in" | "session-error"

/**
 * Where to send a browser that still has no identity user.
 *
 * `pendingThisTab` is set in sessionStorage immediately before leaving for
 * identity, so it only survives a same-tab return. A flag left in
 * localStorage from an older visit is not a return: those users still need
 * identity.lobbyze.com. Identity itself redirects a live session straight
 * back to callbackURL, so a same-tab return signs out first and then opens
 * the sign-in form. The session error page is only the stop when that
 * return cannot be sent to identity again (identity never answered, or
 * sign-out failed and another redirect would loop).
 */
export function signInNavigation(input: {
  pendingThisTab: boolean
  identityUnreachable: boolean
  signOutFailed: boolean
}): SignInNavigation {
  if (!input.pendingThisTab) {
    return "sign-in"
  }
  if (input.identityUnreachable || input.signOutFailed) {
    return "session-error"
  }
  return "sign-out-then-sign-in"
}

export function signInUrl(identityUrl: string, returnTo: string): string {
  const url = new URL("/sign-in", identityUrl)
  url.searchParams.set("callbackURL", returnTo)
  return url.toString()
}

/**
 * Holds the identity JWT in memory and swaps it before it expires. Concurrent
 * callers share one token request. The browser's clock is corrected by the
 * token's iat, so a skewed clock does not send expired tokens.
 */
export class IdentitySession {
  private token: string | null = null
  private user: IdentityUser | null = null
  private skewSeconds = 0
  private inflight: Promise<string | null> | null = null
  private lastLoadFailed = false
  private readonly fetchToken: () => Promise<string | null>
  private readonly clock: () => number

  constructor(fetchToken: () => Promise<string | null>, clock: () => number = () => Date.now() / 1000) {
    this.fetchToken = fetchToken
    this.clock = clock
  }

  get currentUser(): IdentityUser | null {
    return this.user
  }

  get currentToken(): string | null {
    return this.token
  }

  /** True when the last token request could not reach identity. */
  get identityUnreachable(): boolean {
    return this.lastLoadFailed
  }

  async accessToken(force = false): Promise<string | null> {
    if (!force && this.fresh()) {
      return this.token
    }
    if (!this.inflight) {
      this.inflight = this.load().finally(() => {
        this.inflight = null
      })
    }
    return this.inflight
  }

  clear(): void {
    this.token = null
    this.user = null
  }

  private now(): number {
    return this.clock() + this.skewSeconds
  }

  private fresh(): boolean {
    return !!this.token && !!this.user && this.user.exp - this.now() > REFRESH_MARGIN_SECONDS
  }

  private async load(): Promise<string | null> {
    let raw: string | null
    try {
      raw = await this.fetchToken()
    } catch (err) {
      // Identity is unreachable. Keep a token that has not expired yet.
      console.error("[AUTH] identity token request failed", err)
      this.lastLoadFailed = true
      return this.user && this.user.exp > this.now() ? this.token : null
    }
    this.lastLoadFailed = false
    const user = raw ? userFromToken(raw) : null
    if (!raw || !user) {
      this.clear()
      return null
    }
    this.skewSeconds = user.iat ? user.iat - this.clock() : 0
    this.token = raw
    this.user = user
    return raw
  }
}
